/**
 * SeitenGrenze — die Fehlergrenze, die JEDE Route erbt.
 *
 * Hintergrund (12.09.2026): `ErrorBoundary` gab es seit Monaten, benutzt
 * wurde sie in fünf Seiten um einzelne Abschnitte herum. Über den Routen
 * stand keine. Ein Render-Fehler in irgendeiner der 79 Routen nahm damit
 * nicht die Seite mit, sondern die GANZE Anwendung: `#root` war leer,
 * keine Navigation, keine Kopfzeile, kein Hinweis — weiße Seite. Gemessen
 * im Prüfstand an `/admin/users` (`users.map is not a function`); React
 * schrieb in die Konsole „Consider adding an error boundary to your tree".
 *
 * Zwei Ebenen, beide aus dieser Datei:
 *
 *   <Route element={<SeitenGrenze />}>…</Route>
 *       Letzte Instanz um einen ganzen `<Routes>`-Block. Fängt auch das,
 *       was außerhalb jedes Layouts hängt (Rechtstexte, /developers,
 *       Weiterleitungen). Eine weiße Seite kann es damit nicht mehr geben.
 *       Ihre Meldung steht über dem Legal-Footer (`OeffentlicherRahmen`).
 *
 *   <SeitenGrenze>{children}</SeitenGrenze>
 *       Innerhalb der Layout-Hüllen (ProtectedRoute, AdminRoute,
 *       PublicLayoutRoute, AreaRoute) um den Inhalt. Sie greift zuerst —
 *       die nächstgelegene Grenze gewinnt —, deshalb bleiben im Normalfall
 *       Kopfzeile, Navigation und Fußzeile stehen und der Nutzer kann
 *       weiterklicken, statt neu laden zu müssen.
 *
 * Der `key` auf dem Pfad ist nicht schmückend: ohne ihn bliebe die Grenze
 * nach einem Fehler im Fehlerzustand, auch wenn der Nutzer längst auf eine
 * andere Seite gewechselt ist. Der Pfadwechsel hängt eine frische Grenze ein.
 */
import { Outlet, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import OeffentlicherRahmen from './OeffentlicherRahmen';
import { useTranslation } from '../hooks/useTranslation';

// Die letzte Instanz steht ausserhalb jedes Layouts: Greift sie, ist auch der
// Footer weg. Deshalb bringt sie ihn selbst mit (Regel #21) — ohne Kopfzeile,
// damit die Meldung nicht an derselben Stelle scheitert, die geworfen hat.
const mitFooter = meldung => (
  <OeffentlicherRahmen kopf={false} inhaltStyle={{ padding: '24px', maxWidth: '960px', margin: '0 auto' }}>
    {meldung}
  </OeffentlicherRahmen>
);

function SeitenGrenze({ children }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const letzteInstanz = children === undefined;

  return (
    <ErrorBoundary key={pathname} name={pathname} t={t} rahmen={letzteInstanz ? mitFooter : undefined}>
      {letzteInstanz ? <Outlet /> : children}
    </ErrorBoundary>
  );
}

export default SeitenGrenze;
